import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Paper } from "helpers/api";
import { RootState } from "store";

export enum RightSidebarDrawerType {
  SEARCH_STEP = "searchStep",
  PAPER_SIDEBAR = "paperSidebar",
}

interface RightSidebarDrawerState {
  activeDrawer: RightSidebarDrawerType | null;
  searchStepDrawer: {
    interactionId: string | null;
    openedManually: boolean;
  };
  paperSidebarDrawer: {
    paper: Paper | null;
    quote: string | null;
    interactionId: string | null;
  };
}

const initialState: RightSidebarDrawerState = {
  activeDrawer: null,
  searchStepDrawer: {
    interactionId: null,
    openedManually: false,
  },
  paperSidebarDrawer: {
    paper: null,
    quote: null,
    interactionId: null,
  },
};

const rightSidebarDrawerSlice = createSlice({
  name: "rightSidebarDrawer",
  initialState,
  reducers: {
    // SearchStepDrawer actions
    setInteractionId(
      state,
      { payload }: PayloadAction<{ interactionId: string; isManual?: boolean }>
    ) {
      // Close other drawer if open
      if (state.activeDrawer === RightSidebarDrawerType.PAPER_SIDEBAR) {
        state.paperSidebarDrawer.paper = null;
      }

      // Set as active drawer
      state.activeDrawer = RightSidebarDrawerType.SEARCH_STEP;
      state.searchStepDrawer.interactionId = payload.interactionId;
      state.searchStepDrawer.openedManually = payload.isManual ?? false;
    },
    removeInteractionId(state) {
      state.searchStepDrawer.interactionId = null;
      state.searchStepDrawer.openedManually = false;
    },
    // PaperSidebarDrawer actions
    setPaper(
      state,
      { payload }: PayloadAction<{ paper: Paper; interactionId: string }>
    ) {
      // Close other drawer if open
      if (state.activeDrawer === RightSidebarDrawerType.SEARCH_STEP) {
        state.searchStepDrawer.interactionId = null;
      }

      // Set as active drawer
      state.activeDrawer = RightSidebarDrawerType.PAPER_SIDEBAR;
      state.paperSidebarDrawer.paper = payload.paper;
      state.paperSidebarDrawer.interactionId = payload.interactionId;
    },
    removePaper(state) {
      state.paperSidebarDrawer.paper = null;
      state.paperSidebarDrawer.interactionId = null;
    },
    setQuote(state, { payload }: PayloadAction<string | null>) {
      state.paperSidebarDrawer.quote = payload;
    },
    removeQuote(state) {
      state.paperSidebarDrawer.quote = null;
    },
  },
});

// Selectors
export const selectActiveDrawer = (state: RootState) =>
  state.rightSidebarDrawer.activeDrawer;

export const selectSearchStepDrawerInteractionId = (state: RootState) =>
  state.rightSidebarDrawer.searchStepDrawer.interactionId;

export const selectSearchStepDrawerOpenedManually = (state: RootState) =>
  state.rightSidebarDrawer.searchStepDrawer.openedManually;

export const selectSearchStepDrawerPaper = (state: RootState) =>
  state.rightSidebarDrawer.paperSidebarDrawer.paper;

export const selectPaperSidebarDrawerQuote = (state: RootState) =>
  state.rightSidebarDrawer.paperSidebarDrawer.quote;

export const selectPaperSidebarDrawerInteractionId = (state: RootState) =>
  state.rightSidebarDrawer.paperSidebarDrawer.interactionId;

export const selectIsSearchStepDrawerActive = (state: RootState) =>
  state.rightSidebarDrawer.activeDrawer === RightSidebarDrawerType.SEARCH_STEP;

export const selectIsPaperSidebarDrawerActive = (state: RootState) =>
  state.rightSidebarDrawer.activeDrawer ===
  RightSidebarDrawerType.PAPER_SIDEBAR;

export const {
  setInteractionId,
  removeInteractionId,
  setPaper,
  removePaper,
  setQuote,
  removeQuote,
} = rightSidebarDrawerSlice.actions;

export default rightSidebarDrawerSlice.reducer;
